import { useCallback, useMemo, useState } from "react";
import {
  makeToken,
  serializeTokens,
  type Token,
  type TokenKind,
} from "../formula/formulaToken";
import { expressionToTokens } from "../formula/expressionToTokens";

export type FormulaSelection =
  | { type: "saved" }
  | { type: "template" }
  | { type: "custom" };

const seed = (expression: string): Token[] =>
  expressionToTokens(expression) ?? [];

const useFormulaEditor = (savedExpression: string) => {
  const [tokens, setTokens] = useState<Token[]>(() => seed(savedExpression));
  const [baseline, setBaseline] = useState<string>(() =>
    serializeTokens(tokens),
  );
  const [selection, setSelection] = useState<FormulaSelection>({
    type: "saved",
  });

  const expression = useMemo(() => serializeTokens(tokens), [tokens]);
  const isDirty = expression !== baseline;

  const addToken = useCallback((kind: TokenKind, value: string) => {
    setTokens((prev) => [...prev, makeToken(kind, value)]);
  }, []);

  const insertToken = useCallback(
    (kind: TokenKind, value: string, beforeUid: string | null) => {
      setTokens((prev) => {
        const token = makeToken(kind, value);
        if (beforeUid === null) return [...prev, token];

        const index = prev.findIndex((t) => t.uid === beforeUid);
        if (index === -1) return [...prev, token];

        const next = [...prev];
        next.splice(index, 0, token);
        return next;
      });
    },
    [],
  );

  const removeToken = useCallback((uid: string) => {
    setTokens((prev) => prev.filter((t) => t.uid !== uid));
  }, []);

  const reorder = useCallback((activeUid: string, beforeUid: string | null) => {
    setTokens((prev) => {
      const from = prev.findIndex((t) => t.uid === activeUid);
      if (from === -1) return prev;

      const next = [...prev];
      const [moved] = next.splice(from, 1);
      const to =
        beforeUid === null
          ? next.length
          : next.findIndex((t) => t.uid === beforeUid);
      if (to === -1) return prev;

      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setTokens([]), []);

  const selectTemplate = useCallback((templateExpression: string) => {
    const next = seed(templateExpression);
    setTokens(next);
    setBaseline(serializeTokens(next));
    setSelection({ type: "template" });
  }, []);

  const selectCustom = useCallback(() => {
    setTokens([]);
    setBaseline("");
    setSelection({ type: "custom" });
  }, []);

  return {
    tokens,
    expression,
    isDirty,
    selection,
    addToken,
    insertToken,
    removeToken,
    reorder,
    clearAll,
    selectTemplate,
    selectCustom,
  };
};

export default useFormulaEditor;
